import { useCallback, useState } from 'react';
import { qcErrorMessage } from '../../../services/qcApiError';
import { CorrectionFlagStepBody, qualityCheckServices } from '../../../services/qualityCheck';
import { CorrectionFlagView, ReviewerFlagStep } from './correctionFlag';
import { QcRequestState } from './qcRequestState';

/**
 * SKIN-577 — one reviewer step on the visit's correction flag, as a unit: which step's dialog
 * is open, where its request has got to, and the request itself.
 *
 * The twin of `useIdentifiableCorrection`, for the reason that one was extracted: the container
 * owns enough already. Like it, this does not own the visit payload — the server answers the
 * step with the flag as it recorded it, and the container folds that in (`applyFlag`), so there
 * is still one place that decides what a loaded visit says.
 *
 * No re-read is needed: the step's answer is the flag, actors already resolved to names.
 */
export function useCorrectionFlagStep(args: {
    uuidInvestigation: string | undefined;
    uuidVisit: string | undefined;
    onApplied: (flag: CorrectionFlagView) => void;
}): {
    /** The step whose dialog is open, or null when none is. */
    step: ReviewerFlagStep | null;
    request: QcRequestState;
    open: (step: ReviewerFlagStep) => void;
    close: () => void;
    confirm: (body: CorrectionFlagStepBody) => Promise<void>;
} {
    const { uuidInvestigation, uuidVisit, onApplied } = args;
    const [step, setStep] = useState<ReviewerFlagStep | null>(null);
    const [request, setRequest] = useState<QcRequestState>({ status: 'editable' });

    const close = useCallback(() => {
        setStep(null);
        setRequest({ status: 'editable' });
    }, []);

    const confirm = useCallback(async (body: CorrectionFlagStepBody) => {
        if (!uuidInvestigation || !uuidVisit || !step) return;
        setRequest({ status: 'submitting' });
        let flag: CorrectionFlagView;
        try {
            flag = await qualityCheckServices.applyCorrectionFlagStep(uuidInvestigation, uuidVisit, step, body);
        } catch (err: unknown) {
            // The dialog stays open with what was typed; the server's words say why — a
            // resolve refused for an undiagnosed lesion is the one a reviewer will meet.
            setRequest({ status: 'failed', error: qcErrorMessage(err, null) });
            return;
        }
        onApplied(flag);
        close();
    }, [uuidInvestigation, uuidVisit, step, onApplied, close]);

    return { step, request, open: setStep, close, confirm };
}
